import ReactEcharts from 'echarts-for-react';
import { Alert, AlertRecordWithDate, AlertStatus } from 'lib/alert/models';
import React from 'react';
import { MANTY_GREEN, MANTY_LIGHT_GREY, MANTY_RED } from 'styles/theme';
import { differenceInCalendarDays, format, subDays } from 'date-fns';
import { range } from 'lodash';
import { useTranslation } from 'react-i18next';

const dateFormat = 'dd-MM-yyyy';

type Props = {
    firstRecordDate: Date;
    records: AlertRecordWithDate[];
    alert: Alert;
    nbDays: number;
};

const alertStatusToHeatValue = (status: AlertStatus) =>
    status === AlertStatus.TRIGGERED ? 10 : 0;

/**
 * Clean the records so that there is only one record per day.
 * The record choosen will be:
 * - The one whoes status is TRIGGERED
 * - If none are TRIGGERED the latest one
 * It will also return the array sorted by ascendent date.
 * @param records - The records to filter and sort.
 */
const removeDuplicateDays = (
    records: AlertRecordWithDate[],
): AlertRecordWithDate[] => {
    if (records.length < 2) {
        return records;
    }
    const dateMap: Map<string, AlertRecordWithDate> = new Map();

    for (let i = 0; i < records.length; i += 1) {
        const record = records[i];
        const key = format(record.date, dateFormat);
        if (dateMap.has(key)) {
            // The as AlertRecordWithDate is just to force the type to ignore the case where .get return
            // undefined because we already have a .has guard before.
            const prev: AlertRecordWithDate = dateMap.get(
                key,
            ) as AlertRecordWithDate;
            if (
                (prev.status === record.status && record.date > prev.date) ||
                record.status === AlertStatus.TRIGGERED
            ) {
                dateMap.set(key, record);
            }
        } else {
            dateMap.set(key, record);
        }
    }
    const result = Array.from(dateMap.values());
    result.sort((a, b) => a.date.getTime() - b.date.getTime());
    return result;
};

/**
 * Fill the data array with empty days when we don't have a records and
 * return the data in a format that ecahrt heatmap can deal with.
 * @param records - The list of records to take the data from
 * @param nbDays - The total number of days in the output array
 * @returns an array of data in echart format with continuous days.
 */
const fillDays = (
    lastState: AlertStatus,
    records: AlertRecordWithDate[],
    nbDays: number,
    firstRecordDate: Date,
) => {
    const base = range(nbDays)
        .map((d) => [subDays(new Date(), d), -10])
        .reverse();

    let prevStateValue = alertStatusToHeatValue(lastState);
    let recIdx = 0;

    for (let i = 0; i < nbDays; i += 1) {
        if (
            recIdx < records.length &&
            differenceInCalendarDays(base[i][0], records[recIdx].date) === 0
        ) {
            base[i] = [
                records[recIdx].date,
                alertStatusToHeatValue(records[recIdx].status),
            ];
            prevStateValue = alertStatusToHeatValue(records[recIdx].status);
            recIdx += 1;
        } else if (base[i][0] > firstRecordDate) {
            base[i][1] = prevStateValue;
        }
    }

    return base.map((r) => [format(r[0], dateFormat), '', r[1]]);
};

const FixedThresholdEvolutionWithDimension = ({
    records,
    alert,
    nbDays,
    firstRecordDate,
}: Props) => {
    const { t } = useTranslation();
    if (records.length === 0) {
        return <div>{t('alert.status.noDataPeriod')}</div>;
    }

    const statusValueToString = (statusValue: number) => {
        if (statusValue === 0) {
            return t('alert.status.notTriggered');
        }
        if (statusValue > 0) {
            return t('alert.status.triggered');
        }
        return t('alert.status.noData');
    };

    const data = fillDays(
        alert.status,
        removeDuplicateDays(records),
        nbDays,
        firstRecordDate,
    );

    const option = {
        tooltip: {
            formatter: (tooltipData: any) =>
                `<div>${tooltipData.data[0]}: ${statusValueToString(
                    tooltipData.data[2],
                )}<div>`,
        },
        legend: {},
        xAxis: { type: 'category' },
        yAxis: { type: 'category' },
        grid: {
            containLabel: true,
            left: 10,
            right: 10,
            top: 10,
            bottom: 10,
        },
        visualMap: {
            show: false,
            type: 'piecewise',
            pieces: [
                { max: -1, color: MANTY_LIGHT_GREY },
                { min: 0, max: 1, color: MANTY_GREEN },
                { min: 1, color: MANTY_RED },
            ],
        },
        series: [
            {
                type: 'heatmap',
                data,
            },
        ],
    };

    return (
        <ReactEcharts
            style={{ height: 65 }}
            notMerge
            option={option}
            opts={{ renderer: 'canvas' }}
        />
    );
};

export default FixedThresholdEvolutionWithDimension;
